import { useState } from "react";
import { Link } from "react-router-dom";
import BookmarkButton from "./BookmarkButton";
import { Play, Heart, DownloadSimple, MusicNotes, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { creatorHref, audioHref } from "../lib/links";
import LazyImage from "./LazyImage";
import { stylesOf } from "../lib/audioStyles";

export default function AudioRow({ audio, queue = [], onChange, onDeleted }) {
  const { user } = useAuth();
  const { play, current, isPlaying } = usePlayer();
  const [liked, setLiked] = useState(audio.liked_by_me);
  const [likes, setLikes] = useState(audio.likes);
  const [hidden, setHidden] = useState(false);

  const isActive = current?.id === audio.id;
  const canDelete = user?.role === "admin" || user?.id === audio.creator_id;

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) return;
    const { data } = await api.post(`/audios/${audio.id}/like`);
    setLiked(data.liked_by_me);
    setLikes(data.likes);
    onChange?.(data);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${audio.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/audios/${audio.id}`);
      toast.success("Audio deleted");
      setHidden(true);
      onDeleted?.(audio.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  if (hidden) return null;

  return (
    <div
      data-testid={`audio-row-${audio.id}`}
      onClick={() => play(audio, queue)}
      className={`group cursor-pointer flex items-center gap-4 p-4 rounded-sm border transition-all ${
        isActive ? "border-[#880015] bg-gradient-to-r from-[#1A1A1A] to-[#880015]/10" : "border-white/5 bg-[#1A1A1A] hover:border-white/20"
      }`}
    >
      <div className="relative w-14 h-14 rounded-sm overflow-hidden flex-shrink-0 bg-[#232323]">
        <LazyImage
          src={audio.cover_url}
          width={192}
          alt=""
          className="absolute inset-0 w-full h-full"
          imgClassName="w-full h-full object-cover"
          fallback={<div className="w-full h-full flex items-center justify-center"><MusicNotes size={24} className="text-white/30" /></div>}
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
          <Play size={20} weight="fill" className="text-white" />
        </div>
        {isActive && isPlaying && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="flex gap-1 items-end h-5">
              <div className="w-0.5 bg-[#880015] animate-pulse" style={{ height: "60%", animationDelay: "0s" }} />
              <div className="w-0.5 bg-[#880015] animate-pulse" style={{ height: "100%", animationDelay: "0.2s" }} />
              <div className="w-0.5 bg-[#880015] animate-pulse" style={{ height: "70%", animationDelay: "0.4s" }} />
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <Link
          to={audioHref(audio)}
          onClick={(e) => e.stopPropagation()}
          className="font-display font-bold text-sm truncate block hover:text-[#FF8090] transition"
        >
          {audio.title}
        </Link>
        <Link
          to={creatorHref(audio)}
          onClick={(e) => e.stopPropagation()}
          className="text-xs text-white/50 hover:text-white truncate"
        >
          {audio.creator_name}
        </Link>
      </div>

      <div className="max-sm:!hidden flex items-center gap-1.5">
        {stylesOf(audio).map((s) => <span key={s} className="chip chip-garnet">{s}</span>)}
      </div>

      <div className="hidden md:flex items-center gap-4 text-xs font-mono text-white/40 w-32 justify-end">
        <div className="flex items-center gap-1"><DownloadSimple size={12} /> {audio.downloads}</div>
        <div className="flex items-center gap-1"><Play size={12} weight="fill" /> {audio.plays}</div>
      </div>

      <button
        onClick={handleLike}
        data-testid={`audio-like-${audio.id}`}
        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border transition ${
          liked ? "border-[#880015] text-[#FF4060]" : "border-white/10 text-white/60 hover:border-white/30"
        }`}
      >
        <Heart size={14} weight={liked ? "fill" : "regular"} /> {likes}
      </button>

      <BookmarkButton kind="audio" id={audio.id} testId={`audio-bookmark-${audio.id}`} />

      {canDelete && (
        <button
          onClick={handleDelete}
          data-testid={`audio-delete-${audio.id}`}
          title={user?.role === "admin" ? "Admin: delete audio" : "Delete audio"}
          className="flex items-center text-xs p-1.5 rounded-sm border border-white/10 text-white/40 hover:text-white hover:border-[#880015] hover:bg-[#880015] transition"
        >
          <Trash size={14} />
        </button>
      )}
    </div>
  );
}
